import {
  ActionMenu,
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  FormSection,
  LoadingState,
  Mono,
  ResourceTable,
  SectionCard,
  Select,
  StatusBadge,
  TextInput,
  type Column,
} from '@verydia/ui';
import type {
  IssuanceBlocker,
  LicensedProductPlanResource,
  LicenseScope,
  LicenseType,
} from '@verydia/contracts';
import { VerydiaApiError } from '@verydia/sdk';
import { Archive, CircleCheck, Pencil, Plus, RotateCcw, Tags } from 'lucide-react';
import { useState } from 'react';
import { describeFailure, isStarting } from '../lib/api.ts';
import {
  useActivateLicensedProductPlan,
  useArchiveLicensedProductPlan,
  useCreateLicensedProductPlan,
  useLicensedProductPlans,
  useUpdateLicensedProductPlan,
} from '../lib/identity-queries.ts';
import { CopyableId } from './copyable-id.tsx';

const BILLING_INTERVALS = ['ONE_TIME', 'MONTHLY', 'ANNUAL', 'CUSTOM'] as const;

const BILLING_LABEL: Record<string, string> = {
  ONE_TIME: 'One-time',
  MONTHLY: 'Monthly',
  ANNUAL: 'Annual',
  CUSTOM: 'Custom',
};

const STATUS_TONE = { DRAFT: 'neutral', ACTIVE: 'success', ARCHIVED: 'neutral' } as const;
const STATUS_LABEL = { DRAFT: 'Draft', ACTIVE: 'Active', ARCHIVED: 'Archived' } as const;

/** Why a licence cannot be issued yet, in words an operator can act on. */
/**
 * What each readiness blocker means to a person.
 *
 * `satisfies` a complete record of the domain's blockers, so adding one without
 * deciding how it reads fails to compile. The previous version fell back to the
 * raw enum, which would have put `LICENSE_SCOPE_NOT_SET` in front of an operator.
 */
const BLOCKER_LABEL = {
  NOT_ACTIVE: 'not published',
  LICENSE_TYPE_NOT_SET: 'licence type',
  LICENSE_SCOPE_NOT_SET: 'licence scope',
  TERM_NOT_SET: 'term length',
} satisfies Record<IssuanceBlocker, string>;

const blockerLabel = (blocker: string): string | null =>
  (BLOCKER_LABEL as Record<string, string>)[blocker] ?? null;

function codeFromName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

function isConflict(error: unknown): boolean {
  return error instanceof VerydiaApiError && error.code === 'resource_conflict';
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * A recorded price, or the fact that none was recorded.
 *
 * `null` renders as an em dash — never as `$0.00`. No price and a price of zero
 * are different commercial statements: the second says the tier is free, and
 * showing it for the first would put a claim in the vendor's catalog that
 * nobody made.
 */
function formatPrice(minor: number | null, currency: string | null): string {
  if (minor === null || currency === null) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(minor / 100);
}

/**
 * Whether a licence could be issued against this plan.
 *
 * Deliberately separate from status, and deliberately wordy. "Active" and
 * "ready to issue" are two different facts, and an operator who conflates them
 * will believe they can sell something the platform cannot yet deliver.
 */
function IssuanceReadiness({ plan }: { plan: LicensedProductPlanResource }) {
  if (plan.issuance_ready) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[var(--vc-success-text)]">
        <CircleCheck aria-hidden="true" className="size-3.5" />
        Ready
      </span>
    );
  }

  // The specific gaps, so the next step is obvious rather than a guess.
  const missing = plan.issuance_blockers
    .filter((blocker) => blocker !== 'NOT_ACTIVE')
    // A blocker this build has no copy for is dropped rather than shown raw: a
    // console can be older than the API it talks to.
    .map(blockerLabel)
    .filter((label): label is string => label !== null);

  return (
    <span className="text-[var(--vc-text-muted)]">
      Terms not configured
      {missing.length > 0 ? (
        <span className="block text-[11.5px] text-[var(--vc-text-subtle)]">
          Needs {missing.join(', ')}
        </span>
      ) : null}
    </span>
  );
}

/* ---------------------------- issuance template ----------------------------- */

const LICENSE_TYPE_LABEL = {
  SUBSCRIPTION: 'Subscription',
  PERPETUAL: 'Perpetual',
  TRIAL: 'Trial',
  TIME_LIMITED: 'Time limited',
  USAGE_BASED: 'Usage based',
  HYBRID: 'Hybrid',
} satisfies Record<LicenseType, string>;

const LICENSE_SCOPE_LABEL = {
  TENANT: 'Tenant',
  ORGANIZATION: 'Organization',
  USER: 'User',
  DEVICE: 'Device',
  INSTANCE: 'Instance',
  FLOATING_POOL: 'Floating pool',
} satisfies Record<LicenseScope, string>;

/**
 * Mirrors `termRuleFor` in the domain, which is the authority.
 *
 * Three states, not two. PERPETUAL must not carry a term; USAGE_BASED and HYBRID
 * simply do not need one, and a vendor may still choose to bound a metered
 * licence in time. Showing the field only for REQUIRED types would take that
 * choice away without ever saying so.
 */
const TERM_RULE = {
  SUBSCRIPTION: 'REQUIRED',
  TRIAL: 'REQUIRED',
  TIME_LIMITED: 'REQUIRED',
  PERPETUAL: 'FORBIDDEN',
  USAGE_BASED: 'OPTIONAL',
  HYBRID: 'OPTIONAL',
} satisfies Record<LicenseType, 'REQUIRED' | 'OPTIONAL' | 'FORBIDDEN'>;

const termRuleFor = (licenseType: string): 'REQUIRED' | 'OPTIONAL' | 'FORBIDDEN' | 'UNKNOWN' =>
  (TERM_RULE as Record<string, 'REQUIRED' | 'OPTIONAL' | 'FORBIDDEN'>)[licenseType] ?? 'UNKNOWN';

/**
 * The terms a licence issued under this tier would start with.
 *
 * Defaults on a reusable template — not an agreement with anyone. No customer
 * and no environment appears here: those are chosen when a licence is issued,
 * and putting either in this form would turn a template into a contract.
 *
 * Nothing is pre-selected. A licence type nobody chose is not a licence type,
 * and inferring one from the plan's name or price would put a commercial
 * decision into a vendor's catalog that they never made.
 */
function useIssuanceTemplate(plan: LicensedProductPlanResource) {
  // `?? null` rather than `=== null`: a response from an older API, or a fixture
  // written before these fields existed, omits them entirely — and `String(undefined)`
  // is the string "undefined", which fails validation and silently disables Save.
  const asText = (value: number | null | undefined) =>
    value === null || value === undefined ? '' : String(value);

  const [licenseType, setLicenseType] = useState<string>(plan.license_type ?? '');
  const [licenseScope, setLicenseScope] = useState<string>(plan.license_scope ?? '');
  const [termDays, setTermDays] = useState(asText(plan.term_days));
  const [seatLimit, setSeatLimit] = useState(asText(plan.seat_limit));
  const [activationLimit, setActivationLimit] = useState(asText(plan.activation_limit));

  const termRule = termRuleFor(licenseType);
  const needsTerm = termRule === 'REQUIRED';
  /*
    Offered whenever the chosen type permits one at all.

    Not offered while no type is chosen: whether a term is required, optional or
    forbidden is a property OF the type, so the field has no meaning yet — and a
    term entered first could then be silently forbidden by the type chosen after
    it.
  */
  const termOffered = termRule === 'REQUIRED' || termRule === 'OPTIONAL';
  const positive = (raw: string) => {
    if (raw.trim() === '') return { valid: true, value: null };
    const parsed = Number(raw);
    // Zero is not shorthand for unlimited, so it is not accepted as one.
    return { valid: Number.isInteger(parsed) && parsed >= 1, value: parsed };
  };

  const term = positive(termDays);
  const seats = positive(seatLimit);
  const activations = positive(activationLimit);
  const termMissing = needsTerm && term.value === null;

  return {
    valid: term.valid && seats.valid && activations.valid && !termMissing,
    payload: () => ({
      license_type: licenseType === '' ? null : licenseType,
      license_scope: licenseScope === '' ? null : licenseScope,
      // Cleared rather than carried when the type does not take one: a term left
      // behind after switching type is a contradiction hidden in persistence.
      // Cleared only where a term is forbidden. An optional one the vendor chose
      // survives a type change that still permits it.
      term_days: termOffered ? term.value : null,
      seat_limit: seats.value,
      activation_limit: activations.value,
    }),
    fields: (
      <>
        <Field
          label="Licence type"
          description="How a licence issued under this tier ends. Nothing is assumed from the plan’s name or price."
        >
          {(props) => (
            <Select
              {...props}
              value={licenseType}
              onChange={(event) => setLicenseType(event.target.value)}
            >
              <option value="">Not configured</option>
              {Object.entries(LICENSE_TYPE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Licence scope" description="What a single licence is counted against.">
          {(props) => (
            <Select
              {...props}
              value={licenseScope}
              onChange={(event) => setLicenseScope(event.target.value)}
            >
              <option value="">Not configured</option>
              {Object.entries(LICENSE_SCOPE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {/*
          Shown only for types that end with time. A perpetual licence with an
          expiry silently stops working, which is the worst failure a licensing
          system has — so the field is not merely optional there, it is absent.
        */}
        {termOffered ? (
          <Field
            label="Term"
            {...(needsTerm ? { required: true } : {})}
            description={
              needsTerm
                ? 'Days a licence stays valid. Unrelated to the billing interval.'
                : 'Optional for this licence type. Days a licence stays valid, if you want to bound it.'
            }
            error={
              !term.valid
                ? 'Enter a whole number of days, at least 1.'
                : termMissing
                  ? 'Set a term for this licence type.'
                  : undefined
            }
          >
            {(props) => (
              <TextInput
                {...props}
                inputMode="numeric"
                placeholder="365"
                value={termDays}
                onChange={(event) => setTermDays(event.target.value)}
              />
            )}
          </Field>
        ) : null}

        <Field
          label="Seat limit"
          description="Optional. Leave blank if this tier does not meter seats."
          error={!seats.valid ? 'Enter a whole number, at least 1, or leave blank.' : undefined}
        >
          {(props) => (
            <TextInput
              {...props}
              inputMode="numeric"
              placeholder="Not configured"
              value={seatLimit}
              onChange={(event) => setSeatLimit(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Activation limit"
          description="Optional. Recorded on the template — activation enforcement is not available yet."
          error={
            !activations.valid ? 'Enter a whole number, at least 1, or leave blank.' : undefined
          }
        >
          {(props) => (
            <TextInput
              {...props}
              inputMode="numeric"
              placeholder="Not configured"
              value={activationLimit}
              onChange={(event) => setActivationLimit(event.target.value)}
            />
          )}
        </Field>
      </>
    ),
  };
}

/* ---------------------------- commercial fields ----------------------------- */

/**
 * Price, currency and billing interval, shared by create and edit.
 *
 * They live together because they are one decision. They are shared because the
 * edit dialog originally omitted them, which — with a permanent `code` — left a
 * mistyped price uncorrectable without burning the tier's code. One definition
 * means the two forms cannot disagree about what "no price" means again.
 */
function useCommercialFields(initial: {
  listPriceMinor: number | null;
  currency: string | null;
  billingInterval: string | null;
}) {
  const [price, setPrice] = useState(
    initial.listPriceMinor === null ? '' : (initial.listPriceMinor / 100).toFixed(2),
  );
  const [currency, setCurrency] = useState(initial.currency ?? '');
  const [interval, setInterval] = useState(initial.billingInterval ?? '');

  const priceEntered = price.trim() !== '';
  const parsedMajor = priceEntered ? Number(price) : null;
  const priceValid =
    !priceEntered || (Number.isFinite(parsedMajor) && parsedMajor !== null && parsedMajor >= 0);
  const valid = priceValid && (!priceEntered || currency !== '');

  return {
    valid,
    priceValid,
    priceEntered,
    reset: () => {
      setPrice(initial.listPriceMinor === null ? '' : (initial.listPriceMinor / 100).toFixed(2));
      setCurrency(initial.currency ?? '');
      setInterval(initial.billingInterval ?? '');
    },
    /**
     * On create, an untouched field is simply omitted. On edit it must be sent as
     * an explicit `null`, or clearing a price would read as "leave it alone" and
     * the wrong number would stay on the plan.
     */
    payload: (mode: 'create' | 'edit') => {
      const cleared = mode === 'edit';
      return {
        ...(priceEntered
          ? { list_price_minor: Math.round((parsedMajor ?? 0) * 100), currency }
          : cleared
            ? { list_price_minor: null, currency: null }
            : {}),
        ...(interval === ''
          ? cleared
            ? { billing_interval: null }
            : {}
          : { billing_interval: interval }),
      };
    },
    fields: (
      <>
        {/*
          Optional, and genuinely optional.

          A vendor names their tiers long before they price them, and a form that
          insists on a number here would get an invented one.
        */}
        <Field
          label="List price"
          description="Optional. What you charge for this tier — recorded here, never charged by Verydia."
          error={!priceValid ? 'Enter an amount like 149.00, or leave this blank.' : undefined}
        >
          {(props) => (
            <TextInput
              {...props}
              inputMode="decimal"
              placeholder="Leave blank if not set"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
            />
          )}
        </Field>

        {/*
          Only once a price exists. Defaulting a currency would silently record a
          fact about somebody's business that they never stated.
        */}
        {priceEntered ? (
          <Field label="Currency" required description="ISO 4217 code.">
            {(props) => (
              <Select
                {...props}
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
              >
                <option value="">Select a currency…</option>
                {['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CHF', 'JPY'].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}

        <Field label="Billing interval" description="Optional.">
          {(props) => (
            <Select
              {...props}
              value={interval}
              onChange={(event) => setInterval(event.target.value)}
            >
              <option value="">Not set</option>
              {BILLING_INTERVALS.map((option) => (
                <option key={option} value={option}>
                  {BILLING_LABEL[option]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </>
    ),
  };
}

/* --------------------------------- create ---------------------------------- */

function CreatePlanDialog({
  organizationId,
  productId,
  productName,
  open,
  onOpenChange,
}: {
  organizationId: string;
  productId: string;
  productName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeEdited, setCodeEdited] = useState(false);
  const [description, setDescription] = useState('');
  const commercial = useCommercialFields({
    listPriceMinor: null,
    currency: null,
    billingInterval: null,
  });

  const create = useCreateLicensedProductPlan(organizationId, productId);
  const failure = create.isError ? describeFailure(create.error) : null;
  const codeConflict = failure !== null && /already uses the code/i.test(failure.message);

  const effectiveCode = codeEdited ? code.trim() : codeFromName(name);
  // Blank means blank. The product form once accepted an empty effective slug
  // while calling the field required; this does not repeat it.
  const canSubmit =
    name.trim() !== '' && effectiveCode !== '' && commercial.valid && !create.isPending;

  const close = () => {
    create.reset();
    setName('');
    setCode('');
    setCodeEdited(false);
    setDescription('');
    commercial.reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Create a licensed product plan"
      description={`A commercial tier ${productName} can be licensed under. Created as a draft.`}
      footer={
        <>
          <Button onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<Plus />}
            disabled={!canSubmit}
            onClick={() =>
              create.mutate(
                {
                  name: name.trim(),
                  code: effectiveCode,
                  description: description.trim() === '' ? null : description.trim(),
                  // Blank stays blank. Turning it into 0 would record a price of
                  // nothing, which is a claim the operator did not make.
                  ...commercial.payload('create'),
                },
                { onSuccess: close },
              )
            }
          >
            {create.isPending ? 'Creating…' : 'Create plan'}
          </Button>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={(event) => event.preventDefault()}>
        <Field label="Plan name" required description="What your customers see.">
          {(props) => (
            <TextInput
              {...props}
              placeholder="Enterprise"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Code"
          required
          description="Your own software matches on this, and every licence issued records it. Permanent once set."
          error={codeConflict ? failure.message : undefined}
        >
          {(props) => (
            <TextInput
              {...props}
              placeholder="enterprise"
              value={codeEdited ? code : codeFromName(name)}
              onChange={(event) => {
                setCodeEdited(true);
                setCode(event.target.value);
              }}
            />
          )}
        </Field>

        <Field label="Description" description="Optional.">
          {(props) => (
            <TextInput
              {...props}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>

        {commercial.fields}

        {failure !== null && !codeConflict ? (
          <p role="alert" className="text-[12px] text-[var(--vc-danger-text)]">
            {failure.message}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

/* ---------------------------------- edit ----------------------------------- */

function EditPlanDialog({
  organizationId,
  productId,
  plan,
  open,
  onOpenChange,
  onReload,
}: {
  organizationId: string;
  productId: string;
  plan: LicensedProductPlanResource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReload: () => void;
}) {
  const [name, setName] = useState(plan.name);
  const [description, setDescription] = useState(plan.description ?? '');
  const commercial = useCommercialFields({
    listPriceMinor: plan.list_price_minor,
    currency: plan.currency,
    billingInterval: plan.billing_interval,
  });
  const issuance = useIssuanceTemplate(plan);
  const update = useUpdateLicensedProductPlan(organizationId, productId, plan.id);
  const failure = update.isError ? describeFailure(update.error) : null;
  const conflict = isConflict(update.error);

  const close = () => {
    update.reset();
    setName(plan.name);
    setDescription(plan.description ?? '');
    commercial.reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={conflict ? 'This plan changed since you opened it' : `Edit ${plan.name}`}
      description={
        conflict
          ? 'Someone else saved a change while this page was open. Your edit was not applied, and theirs was kept.'
          : 'Changes are recorded in the audit log.'
      }
      footer={
        conflict ? (
          <>
            <Button onClick={close}>Discard my change</Button>
            <Button
              variant="primary"
              icon={<RotateCcw />}
              onClick={() => {
                update.reset();
                onReload();
              }}
            >
              Reload latest version
            </Button>
          </>
        ) : (
          <>
            <Button onClick={close} disabled={update.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={
                update.isPending || name.trim() === '' || !commercial.valid || !issuance.valid
              }
              onClick={() =>
                update.mutate(
                  {
                    expected_version: plan.version,
                    name: name.trim(),
                    description: description.trim() === '' ? null : description.trim(),
                    ...commercial.payload('edit'),
                    ...issuance.payload(),
                  },
                  { onSuccess: close },
                )
              }
            >
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        )
      }
    >
      {conflict ? (
        <p className="text-[13px] text-[var(--vc-text-muted)]">
          Reload to see the current version, then reapply your change if it is still needed.
        </p>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={(event) => event.preventDefault()}>
          <Field label="Plan name" required>
            {(props) => (
              <TextInput
                {...props}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>

          {/*
            Read-only rather than disabled: nobody may change it, including the
            owner reading this. The API refuses a code change outright.
          */}
          <Field
            label="Code"
            description="Permanent. Your own software matches on it, and every licence issued records it."
          >
            {(props) => (
              <TextInput
                {...props}
                readOnly
                value={plan.code}
                className="bg-[var(--vc-surface-sunken)] text-[var(--vc-text-muted)]"
              />
            )}
          </Field>

          <Field label="Description" description="Optional.">
            {(props) => (
              <TextInput
                {...props}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            )}
          </Field>

          <FormSection title="Commercial">{commercial.fields}</FormSection>

          {/*
            Kept in its own section because it answers a different question.
            Price is what a vendor charges; these are the terms a licence issued
            under this tier would carry. Mixing them into one flat list is how an
            operator comes to believe a monthly price implies a 30-day licence.
          */}
          <FormSection title="Issuance terms">{issuance.fields}</FormSection>

          {failure !== null ? (
            <p role="alert" className="text-[12px] text-[var(--vc-danger-text)]">
              {failure.message}
            </p>
          ) : null}
        </form>
      )}
    </Dialog>
  );
}

/* -------------------------------- lifecycle -------------------------------- */

function ActivatePlanDialog({
  organizationId,
  productId,
  plan,
  open,
  onOpenChange,
}: {
  organizationId: string;
  productId: string;
  plan: LicensedProductPlanResource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const activate = useActivateLicensedProductPlan(organizationId, productId, plan.id);
  const failure = activate.isError ? describeFailure(activate.error) : null;
  const close = () => {
    activate.reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={`Publish ${plan.name}?`}
      description="Publishing puts this tier in your active catalog."
      footer={
        <>
          <Button onClick={close} disabled={activate.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<CircleCheck />}
            disabled={activate.isPending}
            onClick={() =>
              activate.mutate({ expected_version: plan.version }, { onSuccess: close })
            }
          >
            {activate.isPending ? 'Publishing…' : 'Publish plan'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[13px] text-[var(--vc-text-muted)]">
        {/*
          Said out loud, because it is the thing an operator will otherwise
          assume. Publishing a tier is a catalog decision; issuing a licence
          needs terms this plan may not carry yet.
        */}
        <p>
          This does not make the plan ready to issue licences. Licence terms are configured
          separately, and issuance stays unavailable until they are.
        </p>
        {failure !== null ? (
          <p role="alert" className="text-[var(--vc-danger-text)]">
            {failure.message}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

function ArchivePlanDialog({
  organizationId,
  productId,
  plan,
  open,
  onOpenChange,
}: {
  organizationId: string;
  productId: string;
  plan: LicensedProductPlanResource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const archive = useArchiveLicensedProductPlan(organizationId, productId, plan.id);
  const failure = archive.isError ? describeFailure(archive.error) : null;
  const close = () => {
    archive.reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={`Archive ${plan.name}?`}
      description="Archived plans are removed from normal active catalog selection. Historical records remain preserved."
      footer={
        <>
          <Button onClick={close} disabled={archive.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            icon={<Archive />}
            disabled={archive.isPending}
            onClick={() => archive.mutate({ expected_version: plan.version }, { onSuccess: close })}
          >
            {archive.isPending ? 'Archiving…' : 'Archive plan'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[13px] text-[var(--vc-text-muted)]">
        <p>
          This plan keeps its identity and its history. It stops appearing where an active plan is
          expected.
        </p>
        {failure !== null ? (
          <p role="alert" className="text-[var(--vc-danger-text)]">
            {failure.message}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/* ---------------------------------- panel ---------------------------------- */

export function PlansPanel({
  organizationId,
  productId,
  productName,
  productIsActive,
}: {
  organizationId: string;
  productId: string;
  productName: string;
  productIsActive: boolean;
}) {
  const plans = useLicensedProductPlans(organizationId, productId);
  const failure = plans.isError ? describeFailure(plans.error) : null;
  const starting = plans.isFetching && isStarting(plans.failureReason ?? plans.error);
  const rows: readonly LicensedProductPlanResource[] = plans.data?.data.data ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<LicensedProductPlanResource | null>(null);
  const [activating, setActivating] = useState<LicensedProductPlanResource | null>(null);
  const [archiving, setArchiving] = useState<LicensedProductPlanResource | null>(null);

  const columns: ReadonlyArray<Column<LicensedProductPlanResource>> = [
    {
      key: 'name',
      header: 'Plan',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-[var(--vc-text)]">{row.name}</span>
          <Mono className="text-[11.5px] text-[var(--vc-text-subtle)]">{row.code}</Mono>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusBadge tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</StatusBadge>
      ),
    },
    {
      key: 'issuance',
      header: 'Licence issuance',
      render: (row) => <IssuanceReadiness plan={row} />,
    },
    {
      key: 'price',
      header: 'List price',
      align: 'right',
      render: (row) => (
        <span className="tabular-nums text-[var(--vc-text-muted)]">
          {formatPrice(row.list_price_minor, row.currency)}
        </span>
      ),
    },
    {
      key: 'interval',
      header: 'Billing',
      render: (row) => (
        <span className="text-[var(--vc-text-muted)]">
          {row.billing_interval === null
            ? '—'
            : (BILLING_LABEL[row.billing_interval] ?? row.billing_interval)}
        </span>
      ),
    },
    {
      key: 'id',
      header: 'Plan ID',
      render: (row) => <CopyableId value={row.id} label={`${row.name} plan id`} />,
    },
    {
      key: 'updated',
      header: 'Updated',
      align: 'right',
      render: (row) => (
        <span className="tabular-nums text-[var(--vc-text-muted)]">
          {formatTimestamp(row.updated_at)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      // Always reachable, whatever the width — the Checkpoint C lesson, applied
      // rather than rediscovered.
      pin: 'right',
      render: (row) =>
        row.status === 'ARCHIVED' ? null : (
          <ActionMenu
            label={`Actions for ${row.name}`}
            items={[
              ...(row.status === 'DRAFT'
                ? [
                    {
                      key: 'activate',
                      label: 'Publish plan',
                      icon: <CircleCheck />,
                      onSelect: () => setActivating(row),
                    },
                  ]
                : []),
              {
                key: 'edit',
                label: 'Edit plan',
                icon: <Pencil />,
                onSelect: () => setEditing(row),
              },
              {
                key: 'archive',
                label: 'Archive plan',
                icon: <Archive />,
                destructive: true,
                onSelect: () => setArchiving(row),
              },
            ]}
          />
        ),
    },
  ];

  return (
    <>
      <SectionCard
        title="Licensed product plans"
        description={`The commercial tiers ${productName} can be licensed under.`}
        actions={
          productIsActive ? (
            <Button variant="primary" icon={<Plus />} onClick={() => setCreateOpen(true)}>
              Create plan
            </Button>
          ) : null
        }
        flush
      >
        {starting ? (
          <LoadingState label="Verydia is starting up" />
        ) : plans.isPending ? (
          <LoadingState label="Loading licensed product plans" />
        ) : failure !== null ? (
          <ErrorState
            title="Could not load licensed product plans"
            description={failure.message}
            requestId={failure.requestId}
            onRetry={() => void plans.refetch()}
          />
        ) : (
          <ResourceTable
            caption={`Licensed product plans of ${productName}`}
            columns={columns}
            rows={[...rows]}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                icon={<Tags />}
                title="No licensed product plans yet"
                description={`Define the commercial licensing tiers ${productName} can later be licensed under. Application-specific feature entitlements stay in your own software.`}
                action={
                  productIsActive ? (
                    <Button variant="primary" icon={<Plus />} onClick={() => setCreateOpen(true)}>
                      Create plan
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        )}
      </SectionCard>

      <CreatePlanDialog
        organizationId={organizationId}
        productId={productId}
        productName={productName}
        open={createOpen}
        onOpenChange={setCreateOpen}
      />

      {editing !== null ? (
        <EditPlanDialog
          key={`edit-${editing.id}-${editing.version}`}
          organizationId={organizationId}
          productId={productId}
          plan={editing}
          open
          onOpenChange={(next) => (next ? undefined : setEditing(null))}
          onReload={() => void plans.refetch()}
        />
      ) : null}

      {activating !== null ? (
        <ActivatePlanDialog
          key={`activate-${activating.id}-${activating.version}`}
          organizationId={organizationId}
          productId={productId}
          plan={activating}
          open
          onOpenChange={(next) => (next ? undefined : setActivating(null))}
        />
      ) : null}

      {archiving !== null ? (
        <ArchivePlanDialog
          key={`archive-${archiving.id}-${archiving.version}`}
          organizationId={organizationId}
          productId={productId}
          plan={archiving}
          open
          onOpenChange={(next) => (next ? undefined : setArchiving(null))}
        />
      ) : null}
    </>
  );
}
